<template>
  <!--
    SX-02 (doc 22 §3, DESIGN-20). The ONE Student "Coming Soon" dialog,
    rendered once at the shell root (App/index.vue) and raised through the
    `app/comingSoon` store module. Geometry is the rendered V2 reference's
    Advisor Coming Soon sheet (`.sheet-backdrop` > `.bottom-sheet
    .advisor-coming-soon`): a centered dialog on desktop, a bottom sheet from
    V2's 620px phone tier down. Variants differ only in icon, copy and actions;
    there is exactly one component.

    It owns no product behaviour: `close` and `tutor` are reported to the
    shell, which hides the dialog and (for `tutor`) navigates.

    V405-C (doc 26 §7): the `advisor` variant is the Home Advisor card's
    popup. The rendered V405 sheet is geometrically identical to the V2 sheet
    this dialog was measured from, so only icon, copy and the second action
    differ: «ادامه با معلم» reports `teacher` instead of dismissing.
  -->
  <div
    class="StudentComingSoonDialog-backdrop"
    data-testid="student-coming-soon-dialog"
    :data-variant="variant"
    :data-reason="variant === 'teacher' ? reason : null"
    @click.self="$emit('close')"
  >
    <!-- `tabindex="-1"` only so the dialog can take focus programmatically on
         open; it never enters the tab order. Escape and the Tab focus trap are
         handled once, on `document`. -->
    <section
      class="StudentComingSoonDialog"
      ref="dialog"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="student-coming-soon-dialog-title"
      aria-describedby="student-coming-soon-dialog-description"
    >
      <span class="StudentComingSoonDialog-handle" aria-hidden="true"></span>
      <div class="StudentComingSoonDialog-head">
        <div class="StudentComingSoonDialog-copy">
          <span class="StudentComingSoonDialog-icon" aria-hidden="true">
            <student-home-icon :name="iconName" :size="23" />
          </span>
          <h2 id="student-coming-soon-dialog-title">{{ title }}</h2>
          <p id="student-coming-soon-dialog-description">{{ body }}</p>
        </div>
        <button
          type="button"
          class="StudentComingSoonDialog-close"
          data-testid="student-coming-soon-close"
          :aria-label="closeLabel"
          @click="$emit('close')"
        >
          <student-home-icon name="close" :size="18" />
        </button>
      </div>
      <div
        class="StudentComingSoonDialog-actions"
        :class="{ 'StudentComingSoonDialog-actions--single': !showTutorAction }"
      >
        <button
          v-if="showTutorAction"
          type="button"
          class="StudentComingSoonDialog-primary"
          data-testid="student-coming-soon-tutor"
          @click="$emit('tutor')"
        >
          {{ $t('studentComingSoonDialog.tutorAction') }}
        </button>
        <button
          v-if="variant === 'advisor'"
          type="button"
          class="StudentComingSoonDialog-secondary"
          data-testid="student-coming-soon-teacher"
          @click="$emit('teacher')"
        >
          {{ $t('studentComingSoonDialog.advisor.teacherAction') }}
        </button>
        <button
          v-else
          type="button"
          class="StudentComingSoonDialog-secondary"
          data-testid="student-coming-soon-dismiss"
          @click="$emit('close')"
        >
          {{ $t('studentComingSoonDialog.dismiss') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default {
  name: 'StudentComingSoonDialog',
  components: { StudentHomeIcon },
  props: {
    variant: {
      type: String,
      default: 'teacher',
      validator: v => ['teacher', 'course', 'advisor'].includes(v),
    },
    // teacher only: 'help' (getting help from a teacher) or 'register'
    // (becoming / registering as a teacher). The register body must never
    // talk about "help from a teacher" (doc 22 §3).
    reason: {
      type: String,
      default: 'help',
      validator: v => ['help', 'register'].includes(v),
    },
    courseLabel: { type: String, default: null },
    // The shell decides whether the "start with the AI tutor" action makes
    // sense here (authenticated student, not already on the tutor route).
    showTutorAction: { type: Boolean, default: false },
    // advisor only: whether the Student teacher capability is ON. The body
    // names the teacher alternative only then (doc 22 H-B2, doc 26 D-C1).
    teacherAvailable: { type: Boolean, default: false },
  },
  computed: {
    copyKey() {
      if (this.variant === 'advisor') return 'studentComingSoonDialog.advisor'
      return this.variant === 'course'
        ? 'studentComingSoonDialog.course'
        : `studentComingSoonDialog.teacher.${this.reason}`
    },
    title() {
      return this.$t(`${this.copyKey}.title`)
    },
    body() {
      if (this.variant === 'advisor' && this.teacherAvailable) {
        return this.$t(`${this.copyKey}.bodyWithTeacher`)
      }
      return this.$t(`${this.copyKey}.body`, {
        courseLabel: this.courseLabel || '',
      })
    },
    iconName() {
      if (this.variant === 'advisor') return 'headset'
      return this.variant === 'course' ? 'book' : 'teacher'
    },
    closeLabel() {
      return this.variant === 'advisor'
        ? this.$t('studentComingSoonDialog.advisor.close')
        : this.$t('studentComingSoonDialog.close')
    },
  },
  mounted() {
    // Focus restoration target: whatever raised the dialog (a card, a tile,
    // a role button). Captured BEFORE focus moves into the dialog.
    this.previouslyFocused =
      document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : null
    document.addEventListener('keydown', this.onKeydown)
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    if (this.$refs.dialog) this.$refs.dialog.focus()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = this.previousBodyOverflow || ''
    const target = this.previouslyFocused
    if (target && typeof target.focus === 'function' && target.isConnected) {
      target.focus()
    }
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('close')
        return
      }
      if (e.key === 'Tab') this.trapFocus(e)
    },
    // Keep Tab / Shift+Tab inside the dialog while it is open.
    trapFocus(e) {
      const root = this.$refs.dialog
      if (!root) return
      const focusable = Array.from(root.querySelectorAll(FOCUSABLE))
      if (focusable.length === 0) {
        e.preventDefault()
        root.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      const inside = root.contains(active)
      if (e.shiftKey) {
        if (!inside || active === first || active === root) {
          e.preventDefault()
          last.focus()
        }
      } else if (!inside || active === last) {
        e.preventDefault()
        first.focus()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Measured from the V2 reference's `.sheet-backdrop` / `.bottom-sheet` /
// `.advisor-coming-soon` / `.advisor-dialog-*` rules (desktop) and their
// `@media (width<=620px)` overrides (phone tier). Colours are the shared
// Borna tokens the reference's CSS variables resolve to.
$cs-backdrop: rgba(7, 20, 71, 0.45); // V2 #07144773

.StudentComingSoonDialog-backdrop {
  position: fixed;
  inset: 0;
  // Above the student bottom nav (40) and the sidebar drawer; this element is
  // a child of the shell root, so nothing traps it in a lower stacking context.
  z-index: 160;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: $cs-backdrop;
  backdrop-filter: blur(4px);
}

.StudentComingSoonDialog {
  outline: none;
  box-sizing: border-box;
  width: min(100%, 560px);
  padding: 14px 23px 23px;
  background: $borna-surface;
  border-radius: 25px;
  // V2 `--shadow`, as measured on the rendered sheet
  box-shadow: 0 18px 50px rgba(7, 20, 71, 0.08);
  color: $borna-text;
  animation: StudentComingSoonDialog-in 0.2s ease-out;
}

@keyframes StudentComingSoonDialog-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
}

.StudentComingSoonDialog-handle {
  display: block;
  width: 42px;
  height: 4px;
  margin: 0 auto 16px;
  border-radius: 99px;
  background: $borna-border;
}

.StudentComingSoonDialog-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
}

.StudentComingSoonDialog-copy {
  flex: 1;
  min-width: 0;
}

.StudentComingSoonDialog-icon {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  margin-bottom: 13px;
  border-radius: 14px;
  color: $borna-indigo-600;
  background: linear-gradient(135deg, $borna-indigo-50, $borna-teal-50);
}

// V2 keeps the sheet heading at the reference's default heading weight (400).
.StudentComingSoonDialog h2 {
  margin: 0 0 7px;
  font-size: 20px;
  font-weight: $borna-fw-regular;
  line-height: 1.6;
  color: $borna-ink;
}

.StudentComingSoonDialog p {
  max-width: 430px;
  margin: 0;
  font-size: 14px;
  line-height: 1.9;
  color: $borna-muted;
}

// V2 `.sheet-head > button`: 34px, r10, canvas fill, no border.
.StudentComingSoonDialog-close {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: $borna-canvas;
  color: $borna-text;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: $borna-indigo-50;
  }
}

.StudentComingSoonDialog-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.StudentComingSoonDialog-actions--single {
  grid-template-columns: 1fr;
}

.StudentComingSoonDialog-primary,
.StudentComingSoonDialog-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 8px;
  min-height: 46px;
  padding: 0 12px;
  font: inherit;
  // V2 `.advisor-dialog-actions .button`, as measured: 12px / 800
  font-size: 12px;
  font-weight: $borna-fw-extrabold;
  border-radius: 14px;
  cursor: pointer;
  transition: transform 0.16s, background 0.16s, border-color 0.16s;
}

.StudentComingSoonDialog-primary {
  color: #fff;
  background: $borna-indigo-600;
  border: 1px solid transparent;
  box-shadow: 0 8px 20px rgba(49, 75, 214, 0.22);

  &:hover,
  &:focus-visible {
    background: $borna-indigo-900;
  }
}

.StudentComingSoonDialog-secondary {
  color: $borna-text;
  background: #fff;
  border: 1px solid $borna-border;

  &:hover,
  &:focus-visible {
    background: $borna-canvas;
  }
}

.StudentComingSoonDialog :focus-visible {
  outline: 2px solid $borna-indigo-600;
  outline-offset: 3px;
}

// V2's phone tier: the dialog becomes a bottom sheet.
@include borna-phone {
  .StudentComingSoonDialog-backdrop {
    align-items: flex-end;
    padding: 12px;
  }

  .StudentComingSoonDialog {
    border-radius: 24px 24px 18px 18px;
    padding: 12px 17px 17px;
  }

  .StudentComingSoonDialog h2 {
    font-size: 18px;
  }

  .StudentComingSoonDialog p {
    font-size: 13px;
  }

  .StudentComingSoonDialog-actions {
    grid-template-columns: 1fr;
  }
}
</style>
